<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import SettingsTabs from '@/Components/UI/SettingsTabs.vue'

defineOptions({ layout: AppLayout })

const { t } = useI18n()

const props = defineProps({
  users: { type: Array, required: true },
})

const SECTIONS = ['dashboard', 'rentals', 'calendar', 'customers', 'fleet', 'fleet_map', 'suppliers', 'reports', 'losses', 'inbox']

const SECTION_LABELS = {
  dashboard: 'Dashboard',
  rentals:   'Rentals',
  calendar:  'Calendar',
  customers: 'Customers',
  fleet:     'Fleet',
  fleet_map: 'Fleet Map',
  suppliers: 'Suppliers',
  reports:   'Reports',
  losses:    'Losses',
  inbox:     'Inbox',
}

const roleStyle = (role) => ({
  admin:  { background: 'var(--infoSoft)',   color: 'var(--info)'   },
  staff:  { background: 'var(--okSoft)',     color: 'var(--ok)'     },
  viewer: { background: 'var(--accentSoft)', color: 'var(--accent)' },
}[role] || { background: 'var(--surfaceAlt)', color: 'var(--inkSoft)' })

const localPerms = ref(
  Object.fromEntries(props.users.map(u => [u.id, { ...u.permissions }]))
)

const processing = ref(new Set())

const isGranted = (userId, section) => localPerms.value[userId]?.[section] !== false

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const toggle = async (user, section) => {
  const key = `${user.id}:${section}`
  if (processing.value.has(key)) return
  const prev = isGranted(user.id, section)
  const next = !prev
  processing.value = new Set([...processing.value, key])
  localPerms.value[user.id] = { ...localPerms.value[user.id], [section]: next }
  try {
    const body = new FormData()
    body.append('section', section)
    body.append('granted', next)
    body.append('authenticity_token', csrfToken())
    const res = await fetch(`/users/${user.id}/permissions`, {
      method: 'PATCH',
      body,
    })
    if (!res.ok) throw new Error(`${res.status}`)
  } catch (e) {
    console.error('toggle failed', e)
    localPerms.value[user.id] = { ...localPerms.value[user.id], [section]: prev }
  } finally {
    processing.value = new Set([...processing.value].filter(k => k !== key))
  }
}

const initials = (name) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
</script>

<template>
  <PageShell :title="t('nav.settings')">
    <SettingsTabs />
    <div class="mb-1">
      <h2 class="text-xl font-semibold text-[color:var(--ink)]">Access Control</h2>
      <p class="mt-1 text-sm text-[color:var(--muted)]">
        Configure which sections each user can access. Admin users always have full access.
      </p>
    </div>

    <div
      class="rounded-xl overflow-hidden"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] min-w-52">User</th>
              <th
                v-for="section in SECTIONS"
                :key="section"
                class="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] min-w-24"
              >
                {{ SECTION_LABELS[section] }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(user, i) in users.filter(u => u.role !== 'admin')"
              :key="user.id"
              :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
                  >
                    {{ initials(user.full_name) }}
                  </div>
                  <div class="min-w-0">
                    <p class="font-semibold text-[color:var(--ink)] truncate">{{ user.full_name }}</p>
                    <span
                      class="inline-block text-xs px-1.5 py-0.5 rounded capitalize font-medium"
                      :style="roleStyle(user.role)"
                    >{{ user.role }}</span>
                  </div>
                </div>
              </td>

              <td
                v-for="section in SECTIONS"
                :key="section"
                class="px-3 py-3 text-center"
              >
                <button
                  type="button"
                  role="switch"
                  :aria-checked="isGranted(user.id, section)"
                  :aria-label="`${user.full_name} — ${SECTION_LABELS[section]}`"
                  class="relative inline-flex w-10 h-6 rounded-full transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 cursor-pointer"
                  :style="{ backgroundColor: isGranted(user.id, section) ? 'var(--accent)' : 'var(--line)' }"
                  @click="toggle(user, section)"
                >
                  <span
                    class="mt-0.5 mx-0.5 inline-block w-5 h-5 rounded-full shadow transition-transform duration-150"
                    :style="{ background: 'var(--surface)' }"
                    :class="isGranted(user.id, section) ? 'translate-x-4' : 'translate-x-0'"
                  ></span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="px-4 py-3 flex items-center gap-5 text-xs text-[color:var(--muted)]"
        :style="{ borderTop: '1px solid var(--line)' }"
      >
        <span class="flex items-center gap-1.5">
          <span class="w-6 h-3.5 rounded-full inline-block" :style="{ background: 'var(--accent)' }"></span>
          Granted
        </span>
        <span class="flex items-center gap-1.5">
          <span class="w-6 h-3.5 rounded-full inline-block" :style="{ background: 'var(--line)' }"></span>
          Denied
        </span>
        <span class="ml-auto">Changes save automatically</span>
      </div>
    </div>
  </PageShell>
</template>
